import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import type {RepoPullsDashboardActions} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_pulls_dashboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Filter} from '@github-ui/filter'
import type {FilterProps} from '@github-ui/filter'
import {hpcAttrs} from '@github-ui/hpc'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {SoftNavOnlyLink as Link, useLocation, useNavigation} from '@github-ui/react-router'
import {PullsList} from '@github-ui/shared-dashboard-lists/pulls/PullsList'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {addStateToQuery, getStateFromQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addLabelFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {useUpdateCompactModeSetting} from '@github-ui/shared-dashboard-surface/hooks/use-update-compact-mode-setting'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {MilestoneIcon, TagIcon} from '@primer/octicons-react'
import {Button, CounterLabel, Pagination} from '@primer/react'
import {useId, useState} from 'react'

import {useRepoPullsDashboardData, useRepoPullsSearch} from '../hooks/use-repo-pulls-dashboard'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import {useRepoPullsQuickFilterItems} from './repo-pulls-quick-filters/use-repo-pulls-quick-filter-actions'

const REPO_PULLS_DEFAULT_SORTING_KEY = 'created'

function isSameLocation(a: {pathname: string; search: string}, b: {pathname: string; search: string}) {
  return a.pathname === b.pathname && a.search === b.search
}

function addRepoStateToQuery(query: string, state: string) {
  const queryWithoutLegacyState = query
    .split(' ')
    .filter(term => term !== 'is:open' && term !== 'is:closed')
    .join(' ')
  return addStateToQuery(queryWithoutLegacyState, state)
}

function getRepoStateFromQuery(query: string) {
  const state = getStateFromQuery(query)
  if (state) return state
  if (query.split(' ').includes('is:open')) return 'open'
  if (query.split(' ').includes('is:closed')) return 'closed'
  return ''
}

export function RepoPullsDashboardContent() {
  const location = useLocation()
  const navigation = useNavigation()
  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))

  return (
    <ErrorBoundary
      boundaryName="RepoPullsDashboardContent"
      fallback={
        <div className="container-xl px-3 px-md-4 px-lg-5 tmp-my-3">
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        </div>
      }
    >
      <RepoPullsDashboardPage isNavigating={isNavigating} />
    </ErrorBoundary>
  )
}

function RepoPullsDashboardSearch({actions}: {actions?: RepoPullsDashboardActions}) {
  const {fetchedQuery, fetchResults, owner, repo} = useRepoPullsSearch()
  const [query, setQuery] = useState(fetchedQuery)
  const [previousFetchedQuery, setPreviousFetchedQuery] = useState(fetchedQuery)
  const providers = useFilterProviders({repositoryScope: owner && repo ? `${owner}/${repo}` : undefined})

  if (previousFetchedQuery !== fetchedQuery) {
    setPreviousFetchedQuery(fetchedQuery)
    setQuery(fetchedQuery)
  }

  const filterProps: FilterProps = {
    id: 'repo-pulls-dashboard-filter',
    label: 'Search pull requests',
    variant: 'input',
    placeholder: 'Search pull requests',
    filterValue: query,
    providers,
    onChange: setQuery,
    onSubmit: filterQuery => fetchResults({q: filterQuery.raw}),
    settings: {groupAndKeywordSupport: true},
  }

  return (
    <div className="d-flex flex-column flex-md-row flex-md-items-center gap-2 tmp-my-3">
      <div className="flex-auto min-width-0" role="search" aria-label="Pull requests">
        <Filter {...filterProps} data-testid="repo-pulls-dashboard-filter" />
      </div>
      <RepoPullsDashboardActions actions={actions} />
    </div>
  )
}

function RepoPullsDashboardActions({actions}: {actions?: RepoPullsDashboardActions}) {
  const disabledReasonId = useId()
  if (!actions) return null

  const {labels, milestones, newPullRequest} = actions
  return (
    <div className="d-flex flex-wrap gap-2">
      {labels ? (
        <Button as={Link} to={labels.url} leadingVisual={TagIcon}>
          Labels <CounterLabel>{labels.count}</CounterLabel>
        </Button>
      ) : null}
      {milestones ? (
        <Button as={Link} to={milestones.url} leadingVisual={MilestoneIcon}>
          Milestones <CounterLabel>{milestones.count}</CounterLabel>
        </Button>
      ) : null}
      {newPullRequest?.url ? (
        <Button as={Link} to={newPullRequest.url} variant="primary">
          {newPullRequest.label}
        </Button>
      ) : newPullRequest?.disabledReason ? (
        <>
          <Button
            variant="primary"
            inactive
            aria-disabled="true"
            aria-describedby={disabledReasonId}
            title={newPullRequest.disabledReason}
          >
            {newPullRequest.label}
          </Button>
          <span id={disabledReasonId} className="sr-only">
            {newPullRequest.disabledReason}
          </span>
        </>
      ) : null}
    </div>
  )
}

function RepoPullsDashboardPage({isNavigating}: {isNavigating: boolean}) {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const {error, isDeferredLoading, pageInfo, pulls, stateCounts} = useRepoPullsDashboardData()
  const {currentPage, fetchedQuery, fetchResults, owner, repo} = useRepoPullsSearch()
  const compactMode = layoutPayload?.compactMode ?? false
  const repositoryNameWithOwner = owner && repo ? `${owner}/${repo}` : 'repository'
  const showRepositoryNavigation = Boolean(layoutPayload?.actions?.labels)
  const quickFilterItems = useRepoPullsQuickFilterItems({
    owner: owner ?? '',
    repo: repo ?? '',
    query: fetchedQuery,
    onQueryChange: query => fetchResults({q: query}),
    showLabels: showRepositoryNavigation,
    showMilestones: showRepositoryNavigation,
    showProjects: showRepositoryNavigation,
  })

  const {emptyListState} = useScreenReaderAnnouncements(
    'pull-requests',
    isDeferredLoading,
    currentPage,
    fetchedQuery,
    REPO_PULLS_DEFAULT_SORTING_KEY,
    pageInfo.totalCount,
    pageInfo.totalPages,
    error ?? undefined,
  )
  const {updateCompactModeSetting} = useUpdateCompactModeSetting(compactMode, 'dashboard_surface_compact_mode', {
    persistAsPullsPreference: true,
  })

  function handleFilterSelect(type: 'repo' | 'author' | 'label' | 'assignee', value: string) {
    let nextQuery = fetchedQuery
    switch (type) {
      case 'repo':
        return
      case 'author':
        nextQuery = addAuthorFilterToQuery(fetchedQuery, value)
        break
      case 'label':
        nextQuery = addLabelFilterToQuery(fetchedQuery, value)
        break
      case 'assignee':
        nextQuery = addAssigneeFilterToQuery(fetchedQuery, value)
        break
    }
    fetchResults({q: nextQuery})
  }

  return (
    <div className="container-xl px-3 px-md-4 px-lg-5">
      <h1 className="sr-only">{`Pull requests: ${repositoryNameWithOwner}`}</h1>
      <RepoPullsDashboardSearch actions={layoutPayload?.actions} />
      <div className="tmp-my-3" {...hpcAttrs()}>
        {error ? (
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        ) : (
          <>
            <PullsList
              pulls={pulls}
              pageInfo={pageInfo}
              compactMode={compactMode}
              onCompactModeChange={updateCompactModeSetting}
              onSortingItemSelect={sort => fetchResults({q: addSortToQuery(fetchedQuery, sort)})}
              onStateFilterSelect={state => fetchResults({q: addRepoStateToQuery(fetchedQuery, state)})}
              sortingItemSelected={getSortFromQuery(fetchedQuery)}
              stateItemSelected={getRepoStateFromQuery(fetchedQuery)}
              stateCounts={stateCounts}
              emptyListState={emptyListState}
              onFilterSelect={handleFilterSelect}
              isLoading={isNavigating}
              isDeferredLoading={isDeferredLoading}
              isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
              defaultSortingKey={REPO_PULLS_DEFAULT_SORTING_KEY}
              showRepository={false}
              actionBarItems={owner && repo ? quickFilterItems : undefined}
            />
            {pageInfo.totalPages > 1 ? (
              <Pagination
                pageCount={pageInfo.totalPages}
                currentPage={pageInfo.currentPage}
                onPageChange={(event, page) => {
                  event.preventDefault()
                  fetchResults({q: fetchedQuery, page})
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
